<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Input } from '@/components/ui/input';
import { formatIdList, parseIdList } from '@/utils/idList';

/**
 * Free-entry list of ids for "is one of" / "is none of" on an integer id column.
 *
 * The text the person typed is kept as-is while they type, so a trailing comma
 * or a half-typed id does not get rewritten under the cursor. The rule value is
 * the parsed id array.
 */
const props = defineProps<{
  modelValue: number[];
}>();

const emit = defineEmits<{
  'update:modelValue': [ids: number[]];
}>();

/** A character that already separates two ids, so a paste next to it needs no comma. */
const ID_SEPARATOR_CHARACTER = /[\s,;]/;

const draftText = ref(formatIdList(props.modelValue));
const parsedDraft = computed(() => parseIdList(draftText.value));

// Only take an outside change (a favorite applied, the operator switched) when
// it disagrees with what the draft already says. Otherwise every keystroke that
// round-trips through the rule would reformat the text.
watch(
  () => props.modelValue,
  (incomingIds) => {
    if (formatIdList(incomingIds) !== formatIdList(parsedDraft.value.ids)) {
      draftText.value = formatIdList(incomingIds);
    }
  },
);

function onDraftInput(text: string | number) {
  draftText.value = String(text);
  emit('update:modelValue', parsedDraft.value.ids);
}

/**
 * A single-line input drops the line breaks in pasted text. Chrome removes them
 * outright, so a spreadsheet column of "34187⏎15822" would arrive as the one id
 * 3418715822. Swap line breaks for commas before the paste lands.
 */
function onPaste(event: ClipboardEvent) {
  const pastedText = event.clipboardData?.getData('text');
  if (!pastedText || !/[\r\n]/.test(pastedText)) {
    return;
  }
  event.preventDefault();
  const inputElement = event.target as HTMLInputElement;
  const joinedText = pastedText.trim().split(/\s*[\r\n]+\s*/).join(', ');
  const selectionStart = inputElement.selectionStart ?? inputElement.value.length;
  const selectionEnd = inputElement.selectionEnd ?? inputElement.value.length;
  const textBefore = inputElement.value.slice(0, selectionStart);
  const textAfter = inputElement.value.slice(selectionEnd);
  // Pasted against an id, the first or last pasted id would join onto it: "15822" + "28359"
  // reads as the one id 1582228359. Add a separator on each side that touches an id.
  const needsSeparatorBefore = textBefore !== '' && !ID_SEPARATOR_CHARACTER.test(textBefore.slice(-1));
  const needsSeparatorAfter = textAfter !== '' && !ID_SEPARATOR_CHARACTER.test(textAfter.charAt(0));
  const insertedText = `${needsSeparatorBefore ? ', ' : ''}${joinedText}${needsSeparatorAfter ? ', ' : ''}`;
  onDraftInput(textBefore + insertedText + textAfter);
  const caretPosition = selectionStart + insertedText.length;
  requestAnimationFrame(() => inputElement.setSelectionRange(caretPosition, caretPosition));
}

const idCountLabel = computed(() => {
  const idCount = parsedDraft.value.ids.length;
  return idCount === 1 ? '1 id' : `${idCount} ids`;
});

const invalidTokensTitle = computed(() => {
  const invalidTokens = parsedDraft.value.invalidTokens;
  return invalidTokens.length > 0 ? `Not ids, so left out: ${invalidTokens.join(', ')}` : undefined;
});
</script>

<template>
  <div class="relative min-w-0 w-full">
    <Input
      :model-value="draftText"
      type="text"
      inputmode="numeric"
      placeholder="Paste ids, e.g. 34187, 15822"
      data-testid="filter-id-list-input"
      class="min-w-0 w-full h-8 text-xs pr-16"
      :aria-invalid="parsedDraft.invalidTokens.length > 0 || undefined"
      :title="invalidTokensTitle"
      tabindex="0"
      @update:model-value="onDraftInput"
      @paste="onPaste"
    />
    <span
      v-if="draftText.trim() !== ''"
      class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] tabular-nums"
      :class="parsedDraft.invalidTokens.length > 0 ? 'text-destructive' : 'text-muted-foreground'"
      data-testid="filter-id-list-count"
    >
      {{ idCountLabel }}
    </span>
  </div>
</template>
